import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Settings2, Loader2, Plus, Pencil, Trash2, Save, ShieldCheck, Building2, Users2, Sparkles, History, Receipt, Upload, Image as ImageIcon, X, Info, Eye, MapPin } from "lucide-react";
import { toast } from "sonner";

const LEER_MOLKEREI = {
  name: "", firmenbuch: "", firmenbuchgericht: "", uid: "", dvr: "",
  kontrollstellennummer: "", betriebsnummer: "", lebensmittelcode: "",
  geschaeftsfuehrer: "", aufsichtsbehoerde: "", kammer: "",
  strasse: "", plz: "", ort: "", land: "Österreich",
  telefon: "", fax: "", email: "", website: "",
  iban: "", bic: "", bank: "",
  lat: 48.0035, lng: 13.4830, zusatzfelder: {},
};

/**
 * Wiederverwendbares Upload-Feld für Molkerei-Bilder (Logo / Icon).
 * - Nur PNG erlaubt (per user-Präferenz)
 * - Größenlimit über `maxKb` konfigurierbar
 * - Zeigt Vorschau des aktuell hinterlegten Bildes (mit Cache-Bust nach Upload)
 * - Lädt via `POST /molkerei/{feld}` hoch und löscht via `DELETE`
 */
const BildUploadFeld = ({
  feld, titel, beschreibung, maxKb, vorhanden, dateiname, groesse,
  onUploaded, onDeleted, disabled, testIdPrefix,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // cacheBust wird nach erfolgreichem Upload erhöht, damit der Browser das neue Bild lädt
  const [cacheBust, setCacheBust] = useState(0);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== "image/png") {
      toast.error("Nur PNG-Dateien sind erlaubt.");
      return;
    }
    if (file.size > maxKb * 1024) {
      toast.error(`Datei zu groß (${Math.round(file.size / 1024)} KB). Maximal ${maxKb} KB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/molkerei/${feld}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${titel} hochgeladen.`);
      setCacheBust(Date.now());
      onUploaded && (await onUploaded());
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`${titel} wirklich entfernen?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/molkerei/${feld}`);
      toast.success(`${titel} entfernt.`);
      onDeleted && (await onDeleted());
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const previewSrc = vorhanden
    ? `${backend}/api/molkerei/${feld}?v=${cacheBust}`
    : null;

  const busy = uploading || deleting;

  return (
    <div className="rounded-lg border p-4 space-y-3" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <ImageIcon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-sm">{titel}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{beschreibung}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div
          className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-[repeating-conic-gradient(hsl(var(--muted))_0_25%,transparent_0_50%)] bg-[length:12px_12px]"
          data-testid={`${testIdPrefix}-preview`}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={titel}
              className="max-h-full max-w-full object-contain p-1"
            />
          ) : (
            <span className="text-[10px] text-muted-foreground text-center px-1">
              Kein {titel.toLowerCase()}
            </span>
          )}
        </div>

        <div className="flex-1 w-full space-y-2">
          {vorhanden && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              {dateiname && (
                <p><span className="font-medium text-foreground">Datei:</span> {dateiname}</p>
              )}
              {groesse != null && (
                <p><span className="font-medium text-foreground">Größe:</span> {(groesse / 1024).toFixed(1)} KB</p>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testIdPrefix}-input`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || busy}
              onClick={() => inputRef.current?.click()}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5 mr-1.5" />
              )}
              {vorhanden ? "Ersetzen" : "Hochladen"}
            </Button>
            {vorhanden && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled || busy}
                onClick={handleDelete}
                data-testid={`${testIdPrefix}-delete-btn`}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {deleting ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5 mr-1.5" />
                )}
                Entfernen
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Nur PNG, max. {maxKb >= 1024 ? `${(maxKb / 1024).toFixed(0)} MB` : `${maxKb} KB`}.
            {disabled && " Nur SysAdmin darf Bilder verwalten."}
          </p>
        </div>
      </div>
    </div>
  );
};

const MolkereiTab = () => {
  const { user } = useAuth();
  const istSysAdmin = user?.rolle === "sysadmin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const laden = useCallback(async () => {
    const res = await api.get("/molkerei");
    setForm({ ...LEER_MOLKEREI, ...res.data });
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async () => {
    if (!form.name?.trim()) { toast.error("Firmenname ist Pflicht."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
      };
      const res = await api.put("/molkerei", payload);
      setForm({ ...LEER_MOLKEREI, ...res.data });
      toast.success("Molkerei-Stammdaten gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const geocodeAdresse = async () => {
    if (!form?.strasse || !form?.ort) {
      toast.error("Straße und Ort werden für die Adress-Suche benötigt.");
      return;
    }
    setGeocoding(true);
    try {
      const res = await api.post("/molkerei/geocode");
      if (res.data?.ok) {
        setForm((f) => ({
          ...f,
          lat: res.data.lat,
          lng: res.data.lng,
          geocoding_display_name: res.data.display_name,
        }));
        toast.success(
          `Adresse gefunden: ${res.data.display_name?.split(",").slice(0, 3).join(",")}${res.data.cached ? " (aus Cache)" : ""}`,
        );
      } else {
        toast.error(
          `Adresse konnte nicht gefunden werden: ${res.data?.fehler || ""}. Bitte Koordinaten manuell eintragen.`,
        );
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setGeocoding(false); }
  };

  if (!form) return <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" />;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" /> Molkerei-Stammdaten
        </CardTitle>
        <CardDescription>
          Zentrale Firmendaten. Werden im PDF-Kopf der Abrechnungen, in ÖNORM-Reihungsdateien und
          als Startpunkt in der Tourenplanung verwendet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-sm font-medium mb-2">Firma</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Firmenname *</Label>
              <Input data-testid="molkerei-name" value={form.name}
                onChange={(e) => setF("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>UID-Nummer</Label>
              <Input data-testid="molkerei-uid" value={form.uid}
                onChange={(e) => setF("uid", e.target.value)} placeholder="ATU12345678" />
            </div>
            <div className="space-y-1.5">
              <Label>DVR-Nummer</Label>
              <Input data-testid="molkerei-dvr" value={form.dvr}
                onChange={(e) => setF("dvr", e.target.value)} placeholder="0000876" />
            </div>
            <div className="space-y-1.5">
              <Label>Firmenbuchnummer</Label>
              <Input data-testid="molkerei-firmenbuch" value={form.firmenbuch}
                onChange={(e) => setF("firmenbuch", e.target.value)} placeholder="FN 123456 x" />
            </div>
            <div className="space-y-1.5">
              <Label>Firmenbuchgericht</Label>
              <Input data-testid="molkerei-firmenbuchgericht" value={form.firmenbuchgericht}
                onChange={(e) => setF("firmenbuchgericht", e.target.value)} placeholder="Landesgericht …" />
            </div>
            <div className="space-y-1.5">
              <Label>BIO-Kontrollstellennummer</Label>
              <Input data-testid="molkerei-kontrollstelle" value={form.kontrollstellennummer}
                onChange={(e) => setF("kontrollstellennummer", e.target.value)} placeholder="AT-BIO-401" />
            </div>
            <div className="space-y-1.5">
              <Label>Betriebsnummer</Label>
              <Input data-testid="molkerei-betriebsnr" value={form.betriebsnummer}
                onChange={(e) => setF("betriebsnummer", e.target.value)} placeholder="OÖ-4123" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Lebensmittelcode / Identitätskennzeichen</Label>
              <Input value={form.lebensmittelcode}
                onChange={(e) => setF("lebensmittelcode", e.target.value)} placeholder="EG 20005" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Geschäftsführung</Label>
              <Input data-testid="molkerei-geschaeftsfuehrer" value={form.geschaeftsfuehrer}
                onChange={(e) => setF("geschaeftsfuehrer", e.target.value)} placeholder="Vor- und Nachname(n), mit Komma getrennt" />
            </div>
            <div className="space-y-1.5">
              <Label>Aufsichtsbehörde</Label>
              <Input data-testid="molkerei-aufsichtsbehoerde" value={form.aufsichtsbehoerde}
                onChange={(e) => setF("aufsichtsbehoerde", e.target.value)} placeholder="Bezirkshauptmannschaft …" />
            </div>
            <div className="space-y-1.5">
              <Label>Kammer / Mitgliedschaft</Label>
              <Input data-testid="molkerei-kammer" value={form.kammer}
                onChange={(e) => setF("kammer", e.target.value)} placeholder="Wirtschaftskammer OÖ" />
            </div>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-sm font-medium">Adresse</p>
            {istSysAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={geocodeAdresse}
                disabled={geocoding || !form.strasse || !form.ort}
                data-testid="molkerei-geocode-button"
                title="Adresse via OpenStreetMap (Nominatim) in Koordinaten umwandeln"
              >
                {geocoding ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />}
                Adresse geocodieren
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Straße & Nummer</Label>
              <Input value={form.strasse} onChange={(e) => setF("strasse", e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>PLZ</Label>
              <Input value={form.plz} onChange={(e) => setF("plz", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Ort</Label>
              <Input value={form.ort} onChange={(e) => setF("ort", e.target.value)} /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label>Land</Label>
              <Input value={form.land} onChange={(e) => setF("land", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Latitude (Karte)</Label>
              <Input type="number" step="0.000001" value={form.lat ?? ""}
                data-testid="molkerei-lat-input"
                onChange={(e) => setF("lat", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Longitude (Karte)</Label>
              <Input type="number" step="0.000001" value={form.lng ?? ""}
                data-testid="molkerei-lng-input"
                onChange={(e) => setF("lng", e.target.value)} />
            </div>
          </div>
          {form.geocoding_display_name && (
            <p className="text-[11px] text-muted-foreground mt-2" data-testid="molkerei-geocoding-info">
              <MapPin className="h-3 w-3 inline mr-1" />
              Automatisch gefunden: <span className="italic">{form.geocoding_display_name}</span>
            </p>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">
            Tipp: Klick auf „Adresse geocodieren“ sucht die Koordinaten via OpenStreetMap.
            Wenn die Adresse nicht gefunden wird (kommt im ländlichen Raum vor), können Lat/Lng
            manuell eingetragen werden. Änderungen synchronisieren die Molkerei-Position auf allen Touren.
          </p>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Kontakt & Bank</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Telefon</Label>
              <Input value={form.telefon} onChange={(e) => setF("telefon", e.target.value)} placeholder="+43 …" /></div>
            <div className="space-y-1.5"><Label>Fax</Label>
              <Input value={form.fax} onChange={(e) => setF("fax", e.target.value)} placeholder="+43 …" /></div>
            <div className="space-y-1.5"><Label>E-Mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Website</Label>
              <Input value={form.website} onChange={(e) => setF("website", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>IBAN</Label>
              <Input value={form.iban} onChange={(e) => setF("iban", e.target.value)} placeholder="AT.." /></div>
            <div className="space-y-1.5"><Label>BIC</Label>
              <Input value={form.bic} onChange={(e) => setF("bic", e.target.value)} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Bank</Label>
              <Input value={form.bank} onChange={(e) => setF("bank", e.target.value)} /></div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Logo &amp; Icon</p>
          <div className="grid grid-cols-1 gap-3">
            <BildUploadFeld
              feld="logo"
              titel="Firmenlogo"
              beschreibung="Erscheint im PDF-Kopf der Abrechnungen sowie auf öffentlichen Seiten (Impressum). Ideal: transparentes PNG, ca. 800×300 px."
              maxKb={2048}
              vorhanden={!!form.logo_id}
              dateiname={form.logo_filename}
              groesse={form.logo_size}
              onUploaded={laden}
              onDeleted={laden}
              disabled={!istSysAdmin}
              testIdPrefix="molkerei-logo"
            />
            <BildUploadFeld
              feld="icon"
              titel="App-Icon"
              beschreibung="Wird für PWA-Startbildschirm und Browser-Tab (Favicon) verwendet. Ideal: quadratisches PNG, mind. 512×512 px."
              maxKb={512}
              vorhanden={!!form.icon_id}
              dateiname={form.icon_filename}
              groesse={form.icon_size}
              onUploaded={laden}
              onDeleted={laden}
              disabled={!istSysAdmin}
              testIdPrefix="molkerei-icon"
            />
            {!istSysAdmin && (
              <div className="flex items-start gap-2 rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Bild-Uploads sind ausschließlich der Rolle <strong>SysAdmin</strong> vorbehalten,
                  da sie das Erscheinungsbild aller PDF-Abrechnungen und der PWA verändern.
                </span>
              </div>
            )}
          </div>
        </div>

        <Button onClick={speichern} disabled={saving} data-testid="molkerei-save-button">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Molkerei-Stammdaten speichern
        </Button>
      </CardContent>
    </Card>
  );
};

const LEER_FIELD = () => ({
  key: "", label: "", typ: "text", andruck_auf_abrechnung: false,
  reihenfolge: 100, aktiv: true,
});

const CustomFieldsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/stammdaten/felder").then((res) => setRows(res.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    if (!form.key || !form.label) {
      toast.error("Schlüssel und Label sind Pflichtfelder.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, reihenfolge: Number(form.reihenfolge) };
      if (form.id) await api.put(`/stammdaten/felder/${form.id}`, payload);
      else await api.post("/stammdaten/felder", payload);
      toast.success("Feld gespeichert.");
      setForm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const loeschen = async (id) => {
    if (!window.confirm("Feld wirklich löschen? Bestehende Werte in Lieferanten-Datensätzen bleiben zwar erhalten, werden aber nicht mehr angezeigt.")) return;
    try {
      await api.delete(`/stammdaten/felder/${id}`);
      toast.success("Feld gelöscht.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Stammdaten-Parameter</CardTitle>
            <CardDescription>
              Zentrale Liste zusätzlicher Lieferanten-Eigenschaften. Felder mit aktivem Andruck-Flag erscheinen auf der Abrechnung.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setForm(LEER_FIELD())} data-testid="custom-field-create-button">
            <Plus className="h-4 w-4 mr-1" /> Feld anlegen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" /> : (
          <Table data-testid="custom-fields-table">
            <TableHeader>
              <TableRow>
                <TableHead>Reihenfolge</TableHead>
                <TableHead>Schlüssel</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Andruck</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-num">{r.reihenfolge}</TableCell>
                  <TableCell className="font-mono text-xs">{r.key}</TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell><span className="text-xs uppercase text-muted-foreground">{r.typ}</span></TableCell>
                  <TableCell>{r.andruck_auf_abrechnung ? "Ja" : "Nein"}</TableCell>
                  <TableCell>{r.aktiv ? "Ja" : "Nein"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => setForm({ ...r })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => loeschen(r.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="custom-field-dialog">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Feld bearbeiten" : "Neues Feld"}</DialogTitle>
            <DialogDescription>
              Legen Sie ein zusätzliches Lieferanten-Feld an (z. B. Kontrollstellennummer).
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Schlüssel *</Label>
                <Input data-testid="custom-field-key" value={form.key} disabled={!!form.id}
                  onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\W/g, "_").toLowerCase() })}
                  placeholder="kontrollstellennummer" />
              </div>
              <div className="space-y-1.5">
                <Label>Label *</Label>
                <Input data-testid="custom-field-label" value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Kontrollstellennummer" />
              </div>
              <div className="space-y-1.5">
                <Label>Typ</Label>
                <Select value={form.typ} onValueChange={(v) => setForm({ ...form, typ: v })}>
                  <SelectTrigger data-testid="custom-field-typ"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Zahl</SelectItem>
                    <SelectItem value="date">Datum</SelectItem>
                    <SelectItem value="boolean">Ja/Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Reihenfolge</Label>
                <Input type="number" value={form.reihenfolge}
                  onChange={(e) => setForm({ ...form, reihenfolge: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-6 col-span-2">
                <Switch data-testid="custom-field-andruck" checked={form.andruck_auf_abrechnung}
                  onCheckedChange={(v) => setForm({ ...form, andruck_auf_abrechnung: v })} />
                <Label>Andruck auf Abrechnung</Label>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Switch checked={form.aktiv}
                  onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
                <Label>Aktiv</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
            <Button data-testid="custom-field-save" onClick={speichern} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const SystemTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Compliance-Config separat pflegen (eigener Endpoint)
  const [complForm, setComplForm] = useState(null);
  const [savingCompl, setSavingCompl] = useState(false);

  useEffect(() => {
    api.get("/einstellungen/system").then((res) => setForm(res.data));
    api.get("/compliance/konfiguration").then((res) => setComplForm(res.data)).catch(() => {});
  }, []);

  const speichern = async () => {
    setSaving(true);
    try {
      await api.put("/einstellungen/system", form);
      toast.success("Einstellungen gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const speichernCompl = async () => {
    setSavingCompl(true);
    try {
      const res = await api.put("/compliance/konfiguration", complForm);
      setComplForm(res.data);
      toast.success("Qualitäts-Regeln gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingCompl(false); }
  };

  if (!form) return <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" />;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">System-Einstellungen</CardTitle>
          <CardDescription>Zentrale Schalter für Freigabeprozess und Compliance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex-1 pr-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <p className="font-medium">4-Augen-Prinzip bei Abrechnungs-Freigabe</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Wenn aktiv, muss jede Abrechnung durch zwei unterschiedliche Admin-Benutzer freigegeben werden,
                bevor sie für Lieferanten sichtbar wird.
              </p>
            </div>
            <Switch data-testid="setting-vier-augen"
              checked={form.vier_augen_aktiv}
              onCheckedChange={(v) => setForm({ ...form, vier_augen_aktiv: v })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex-1 pr-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <p className="font-medium">4-Augen-Prinzip bei Probenwert-Änderungen</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Wenn aktiv, muss jede Änderung an Milchproben-Werten (Fett, Eiweiß, Zellzahl, Keimzahl usw.)
                durch einen zweiten Administrator freigegeben werden. Bis zur Freigabe bleibt der Originalwert aktiv.
              </p>
            </div>
            <Switch data-testid="setting-vier-augen-labor"
              checked={form.vier_augen_labor}
              onCheckedChange={(v) => setForm({ ...form, vier_augen_labor: v })} />
          </div>
          <Button onClick={speichern} disabled={saving} data-testid="system-settings-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </CardContent>
      </Card>

      {/* Neue Sektion: Qualitäts-Anzeige */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" /> Qualitäts-Anzeige
          </CardTitle>
          <CardDescription>
            Steuert, wie viele Monate rückwirkend als &bdquo;aktuelle Verstöße&ldquo; in Dashboards, der Lieferantenübersicht
            und im Detail-Dialog angezeigt werden. Ältere Verstöße erscheinen im Historie-Bereich.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!complForm ? (
            <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" />
          ) : (
            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">Zeitraum für aktuelle Verstöße</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Verstöße innerhalb dieses Zeitraums werden auf der Lieferantenübersicht als Alarm-Badge
                    hervorgehoben und im Detail-Dialog getrennt von der Historie dargestellt.
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className="font-num font-semibold text-2xl text-primary"
                    data-testid="compliance-zeitraum-value"
                  >
                    {complForm.verstoss_anzeige_monate}
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {complForm.verstoss_anzeige_monate === 1 ? "Monat" : "Monate"}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Slider
                  data-testid="compliance-zeitraum-slider"
                  min={1} max={24} step={1}
                  value={[complForm.verstoss_anzeige_monate]}
                  onValueChange={(v) => setComplForm({ ...complForm, verstoss_anzeige_monate: v[0] })}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground font-num">
                  <span>1 Monat</span>
                  <span>12 Monate</span>
                  <span>24 Monate</span>
                </div>
              </div>
              <Button
                onClick={speichernCompl}
                disabled={savingCompl}
                data-testid="compliance-zeitraum-save"
              >
                {savingCompl ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Zeitraum speichern
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const TestdatenTab = () => {
  const [ziel, setZiel] = useState(150);
  const [running, setRunning] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  const [monateHistorie, setMonateHistorie] = useState(24);
  const [runningHist, setRunningHist] = useState(false);
  const [ergebnisHist, setErgebnisHist] = useState(null);

  const [monateAbr, setMonateAbr] = useState(12);
  const [runningAbr, setRunningAbr] = useState(false);
  const [ergebnisAbr, setErgebnisAbr] = useState(null);

  const erweitern = async () => {
    if (!window.confirm(`Testdaten auf ${ziel} Lieferanten erweitern? Neue Bauern werden im Innviertel (Ried + Braunau) angelegt.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/testdaten/erweitern", { ziel_gesamt: ziel });
      setErgebnis(data);
      toast.success(`${data.neu_angelegt} Lieferanten neu angelegt.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  const historisieren = async () => {
    if (!window.confirm(`Historische Lieferungen und Laborwerte für die letzten ${monateHistorie} Monate erzeugen? Kann bei 24 Monaten mehrere Minuten dauern.`)) return;
    setRunningHist(true);
    try {
      const { data } = await api.post("/testdaten/historisieren", { monate_zurueck: monateHistorie }, { timeout: 600000 });
      setErgebnisHist(data);
      toast.success(`${data.lieferungen_neu} Lieferungen · ${data.laborwerte_neu} Laborwerte erzeugt.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunningHist(false); }
  };

  const abrechnungen = async () => {
    if (!window.confirm(`Historische Abrechnungen für die letzten ${monateAbr} Monate erzeugen? Status-Mix (freigegeben / entwurf / storniert) wird automatisch gesetzt.`)) return;
    setRunningAbr(true);
    try {
      const { data } = await api.post("/testdaten/abrechnungen", { monate: monateAbr }, { timeout: 600000 });
      setErgebnisAbr(data);
      toast.success(`${data.freigaben} Freigaben · ${data.stornos} Stornos · ${data.entwuerfe_verbleibend} Entwürfe.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunningAbr(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users2 className="h-5 w-5 text-primary" /> Testdaten-Erweiterung (Lieferanten)
          </CardTitle>
          <CardDescription>
            Legt idempotent zusätzliche Lieferanten mit realistischen Kuhzahlen (30–150) und Lieferungen an.
            Neue Bauern werden im Innviertel (Bezirke Ried und Braunau) verteilt.
            Doppelte Aufrufe werden übersprungen, sobald das Ziel erreicht ist.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Ziel-Gesamtanzahl Lieferanten</Label>
              <Input type="number" min={10} max={500} value={ziel}
                onChange={(e) => setZiel(Number(e.target.value))}
                data-testid="testdaten-ziel" />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <Button onClick={erweitern} disabled={running} data-testid="testdaten-erweitern-btn">
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Testdaten erweitern
              </Button>
            </div>
          </div>
          {ergebnis && (
            <div className="rounded-md border bg-secondary/50 p-3 text-sm space-y-1">
              <p><strong>Gesamt:</strong> {ergebnis.gesamt} Lieferanten</p>
              <p><strong>Neu angelegt:</strong> {ergebnis.neu_angelegt}</p>
              {ergebnis.lieferungen_neu != null && (
                <p><strong>Lieferungen:</strong> {ergebnis.lieferungen_neu} · <strong>Laborwerte:</strong> {ergebnis.laborwerte_neu}</p>
              )}
              {ergebnis.hinweis && (
                <p className="text-xs text-muted-foreground">{ergebnis.hinweis}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-5 w-5 text-primary" /> Historische Lieferungen &amp; Laborwerte
          </CardTitle>
          <CardDescription>
            Erzeugt Lieferungen und Milchproben für alle aktiven Lieferanten für die letzten N Monate rückwirkend.
            Idempotent: bereits vorhandene (Lieferant, Datum)-Paare werden übersprungen.
            <br />
            <span className="text-amber-600 dark:text-amber-400">
              Hinweis: bei 24 Monaten werden ca. 40.000–70.000 Lieferungen und 5.000–15.000 Laborwerte erzeugt.
              Die Verarbeitung kann 30–90 Sekunden dauern.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Monate rückwirkend</Label>
              <Input type="number" min={1} max={60} value={monateHistorie}
                onChange={(e) => setMonateHistorie(Number(e.target.value))}
                data-testid="testdaten-historie-monate" />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <Button onClick={historisieren} disabled={runningHist} data-testid="testdaten-historisieren-btn">
                {runningHist ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <History className="h-4 w-4 mr-2" />}
                Historie erzeugen
              </Button>
            </div>
          </div>
          {ergebnisHist && (
            <div className="rounded-md border bg-secondary/50 p-3 text-sm space-y-1">
              <p><strong>Zeitraum:</strong> {ergebnisHist.zeitraum?.von} bis {ergebnisHist.zeitraum?.bis}</p>
              <p><strong>Lieferanten verarbeitet:</strong> {ergebnisHist.lieferanten}</p>
              <p><strong>Neue Lieferungen:</strong> {ergebnisHist.lieferungen_neu?.toLocaleString("de-AT")} · <strong>Neue Laborwerte:</strong> {ergebnisHist.laborwerte_neu?.toLocaleString("de-AT")}</p>
              {ergebnisHist.uebersprungen_bereits_vorhanden > 0 && (
                <p className="text-xs text-muted-foreground">
                  {ergebnisHist.uebersprungen_bereits_vorhanden.toLocaleString("de-AT")} Tage übersprungen (bereits vorhanden)
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-5 w-5 text-primary" /> Historische Abrechnungen
          </CardTitle>
          <CardDescription>
            Erzeugt Monatsabrechnungen für alle Lieferanten mit realistischem Status-Mix:
            ältere Monate 100% freigegeben, mittlere mit vereinzelten Stornos + Gegenbuchungen,
            aktuelle Monate überwiegend als Entwurf.
            Idempotent: bereits freigegebene Abrechnungen werden nicht überschrieben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Monate rückwirkend</Label>
              <Input type="number" min={1} max={36} value={monateAbr}
                onChange={(e) => setMonateAbr(Number(e.target.value))}
                data-testid="testdaten-abrechnungen-monate" />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <Button onClick={abrechnungen} disabled={runningAbr} data-testid="testdaten-abrechnungen-btn">
                {runningAbr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
                Abrechnungen erzeugen
              </Button>
            </div>
          </div>
          {ergebnisAbr && (
            <div className="rounded-md border bg-secondary/50 p-3 text-sm space-y-1">
              <p><strong>Monate bearbeitet:</strong> {ergebnisAbr.monate_bearbeitet} ({ergebnisAbr.monate_liste?.[0]}{ergebnisAbr.monate_liste?.length > 1 ? ` – ${ergebnisAbr.monate_liste?.[ergebnisAbr.monate_liste.length - 1]}` : ""})</p>
              <p><strong>Freigaben:</strong> {ergebnisAbr.freigaben} · <strong>Stornos:</strong> {ergebnisAbr.stornos} · <strong>Entwürfe verbleibend:</strong> {ergebnisAbr.entwuerfe_verbleibend}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default function AdminEinstellungenPage() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Einstellungen</h1>
        <p className="text-sm text-muted-foreground">
          System-Verhalten, Stammdaten-Parameter und weitere zentrale Konfigurationen.
        </p>
      </div>
      <Tabs defaultValue="molkerei">
        <TabsList>
          <TabsTrigger value="molkerei" data-testid="settings-tab-molkerei">Molkerei</TabsTrigger>
          <TabsTrigger value="system" data-testid="settings-tab-system">System</TabsTrigger>
          <TabsTrigger value="felder" data-testid="settings-tab-felder">Stammdaten-Parameter</TabsTrigger>
          <TabsTrigger value="testdaten" data-testid="settings-tab-testdaten">Testdaten</TabsTrigger>
        </TabsList>
        <TabsContent value="molkerei" className="mt-4"><MolkereiTab /></TabsContent>
        <TabsContent value="system" className="mt-4"><SystemTab /></TabsContent>
        <TabsContent value="felder" className="mt-4"><CustomFieldsTab /></TabsContent>
        <TabsContent value="testdaten" className="mt-4"><TestdatenTab /></TabsContent>
      </Tabs>
    </div>
  );
}
